import type { FormFieldProps } from 'comp/screenBuilder/comp/formBuilder/types/formPropsTypes';
import type { InputOption, TableColumnConfig } from 'comp/screenBuilder/comp/formBuilder/types/formConfigTypes';
import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { Button, Form, Input, InputNumber, Select, Space, Table, Tooltip } from 'antd';
import FontAwesomeIcon from 'comp/wraps/faIcon/FAIcon';
import { FormattedMessage } from 'react-intl';
import useTableApiOptions from './hooks/useTableApiOptions';

type TableFieldProps = FormFieldProps & {
  editable: boolean;
};

function TableField({ field, formFieldName, removeFormItemLabel, editable }: TableFieldProps): JSX.Element {
  const { formItem, formInput } = field;
  const { label } = formItem;
  const tableColumns = formInput.context?.tableColumns ?? [];
  const apiOptions = useTableApiOptions(formInput.context?.optionsApi);

  const collapsible = !!formInput.context?.collapsible;
  const defaultCollapsed = !!formInput.context?.defaultCollapsed;
  const [collapsedOverride, setCollapsedOverride] = useState<boolean | undefined>(undefined);

  const initialRow = useMemo(() => {
    const row: Record<string, unknown> = {};
    tableColumns.forEach((column) => {
      if (column.inputType === 'number') {
        row[column.name] = undefined;
      } else if (column.inputType === 'timestamp') {
        row[column.name] = null;
      } else {
        row[column.name] = '';
      }
    });
    return row;
  }, [tableColumns]);

  function getColumnOptions(column: TableColumnConfig): InputOption[] {
    if (column.optionsSource === 'api') {
      return apiOptions;
    }

    return column.options ?? formInput.options ?? [];
  }

  function renderColumnInput(
    column: TableColumnConfig,
    row: { listField: { name: number; fieldKey: number } }
  ): JSX.Element {
    const rules = column.required ? [{ required: true }] : undefined;
    const isDisabled = !editable || !!column.disabled;

    if (column.inputType === 'select') {
      return (
        <Form.Item shouldUpdate noStyle>
          {({ getFieldValue }) => {
            const rowValue = getFieldValue([...formFieldName, row.listField.name]) as
              | Record<string, unknown>
              | undefined;
            const hasTimestamp = !!rowValue?.timeStamp;
            const hasSupplier = !!rowValue?.supplier;
            const isSupplierLocked = column.name === 'supplier' && hasTimestamp && hasSupplier;
            const shouldDisableSelect = isDisabled || isSupplierLocked;

            if (shouldDisableSelect) {
              const rawValue = getFieldValue([...formFieldName, row.listField.name, column.name]) as string | undefined;
              const selectedLabel = getColumnOptions(column).find((opt) => opt.key === rawValue)?.value ?? '';

              return (
                <Input
                  disabled
                  value={selectedLabel}
                  style={{ width: '100%', color: '#000', WebkitTextFillColor: '#000' }}
                />
              );
            }

            return (
              <Form.Item
                name={[row.listField.name, column.name]}
                fieldKey={[row.listField.fieldKey, column.name]}
                rules={rules}
                style={{ marginBottom: 0 }}
              >
                <Select showSearch allowClear optionFilterProp='children' disabled={false}>
                  {getColumnOptions(column).map(({ key, value }) => (
                    <Select.Option key={key} value={key}>
                      {value}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            );
          }}
        </Form.Item>
      );
    }

    if (column.inputType === 'number') {
      return (
        <Form.Item shouldUpdate noStyle>
          {({ getFieldValue }) => {
            const currencyValue = column.currency ? (getFieldValue(column.currency) as string | undefined) : undefined;
            const rawValue = getFieldValue([...formFieldName, row.listField.name, column.name]) as number | undefined;
            const displayValue = rawValue === null || rawValue === undefined ? '' : String(rawValue);

            if (isDisabled) {
              return (
                <Space size={0} style={{ width: '150px' }} align='start'>
                  {currencyValue && (
                    <Input
                      value={currencyValue}
                      disabled
                      style={{
                        width: 42,
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0,
                        color: '#000',
                        WebkitTextFillColor: '#000',
                      }}
                    />
                  )}
                  <Input
                    value={displayValue}
                    disabled
                    style={{
                      width: '100%',
                      ...(currencyValue ? { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 } : undefined),
                      color: '#000',
                      WebkitTextFillColor: '#000',
                    }}
                  />
                </Space>
              );
            }

            return (
              <Space size={0} style={{ width: '150px' }} align='start'>
                {currencyValue && (
                  <Input
                    value={currencyValue}
                    disabled
                    style={{
                      width: 42,
                      borderTopRightRadius: 0,
                      borderBottomRightRadius: 0,
                    }}
                  />
                )}
                <Form.Item
                  name={[row.listField.name, column.name]}
                  fieldKey={[row.listField.fieldKey, column.name]}
                  rules={rules}
                  style={{ marginBottom: 0, flex: 1 }}
                >
                  <InputNumber
                    min={column.min}
                    style={{
                      width: '100%',
                      ...(currencyValue ? { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 } : undefined),
                    }}
                    disabled={isDisabled}
                  />
                </Form.Item>
              </Space>
            );
          }}
        </Form.Item>
      );
    }

    if (column.inputType === 'timestamp') {
      return (
        <Form.Item
          name={[row.listField.name, column.name]}
          fieldKey={[row.listField.fieldKey, column.name]}
          rules={rules}
          style={{ marginBottom: 0, width: '150px' }}
        >
          <TimestampInput
            disabled={isDisabled}
            style={isDisabled ? { color: '#000', WebkitTextFillColor: '#000' } : undefined}
          />
        </Form.Item>
      );
    }

    return (
      <Form.Item shouldUpdate noStyle>
        {({ getFieldValue }) => {
          const currentValue = getFieldValue([...formFieldName, row.listField.name, column.name]) as string | undefined;
          const tooltipText = currentValue?.trim() ? currentValue : undefined;

          return (
            <Tooltip title={tooltipText}>
              <Form.Item
                name={[row.listField.name, column.name]}
                fieldKey={[row.listField.fieldKey, column.name]}
                rules={rules}
                style={{ marginBottom: 0 }}
              >
                <Input
                  disabled={isDisabled}
                  style={isDisabled ? { color: '#000', WebkitTextFillColor: '#000' } : undefined}
                />
              </Form.Item>
            </Tooltip>
          );
        }}
      </Form.Item>
    );
  }

  function hasValue(value: unknown): boolean {
    if (value === null || value === undefined) {
      return false;
    }

    if (typeof value === 'string') {
      return value.trim().length > 0;
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    return true;
  }

  function listHasAnyValue(listValue: unknown): boolean {
    const rows = Array.isArray(listValue) ? (listValue as unknown[]) : [];
    return rows.some((row) => {
      if (!row || typeof row !== 'object') {
        return false;
      }
      return Object.values(row as Record<string, unknown>).some((v) => hasValue(v));
    });
  }

  return (
    <Form.Item
      label={
        !removeFormItemLabel &&
        label && (
          <Space size={8}>
            <FormattedMessage {...label} />
            <Form.Item shouldUpdate noStyle>
              {({ getFieldValue }) => {
                if (!collapsible) {
                  return null;
                }

                const currentListValue = getFieldValue(formFieldName);
                const hasAny = listHasAnyValue(currentListValue);
                const collapsed = collapsedOverride ?? (defaultCollapsed && !hasAny);

                return (
                  <Button type='link' size='small' onClick={() => setCollapsedOverride((prev) => !(prev ?? collapsed))}>
                    <FormattedMessage
                      id={collapsed ? 'table.show' : 'table.hide'}
                      defaultMessage={collapsed ? 'Show' : 'Hide'}
                    />
                  </Button>
                );
              }}
            </Form.Item>
          </Space>
        )
      }
      required={false}
    >
      <Form.List name={formFieldName}>
        {(fields, { add, remove }) => {
          const rows = fields.map((listField) => ({ key: listField.key, listField }));
          return (
            <Space direction='vertical' style={{ width: '100%' }} size={8}>
              <Form.Item shouldUpdate noStyle>
                {({ getFieldValue }) => {
                  const currentListValue = getFieldValue(formFieldName);
                  const hasAny = listHasAnyValue(currentListValue);
                  const collapsed = !collapsible ? false : collapsedOverride ?? (defaultCollapsed && !hasAny);

                  return (
                    <>
                      {(!collapsible || !collapsed) && (
                        <Table
                          pagination={false}
                          size='small'
                          dataSource={rows}
                          rowKey='key'
                          columns={[
                            ...tableColumns.map((column) => ({
                              title: <FormattedMessage {...column.label} />,
                              key: column.name,
                              render: (_: unknown, row: { listField: { name: number; fieldKey: number } }) =>
                                renderColumnInput(column, row),
                            })),
                            ...(editable
                              ? [
                                  {
                                    title: '',
                                    key: 'actions',
                                    width: 56,
                                    render: (_: unknown, row: { listField: { name: number } }) => (
                                      <Form.Item shouldUpdate noStyle>
                                        {({ getFieldValue: getRowValue }) => {
                                          const rowValue = getRowValue([...formFieldName, row.listField.name]) as
                                            | Record<string, unknown>
                                            | undefined;
                                          const rowHasAnyValue =
                                            !!rowValue && Object.values(rowValue).some((value) => hasValue(value));

                                          if (rowHasAnyValue) {
                                            return null;
                                          }

                                          return (
                                            <Button
                                              type='text'
                                              icon={<FontAwesomeIcon icon='trash' />}
                                              aria-label='Remove row'
                                              onClick={() => remove(row.listField.name)}
                                            />
                                          );
                                        }}
                                      </Form.Item>
                                    ),
                                  },
                                ]
                              : []),
                          ]}
                        />
                      )}
                    </>
                  );
                }}
              </Form.Item>
              {editable && (
                <Button
                  type='dashed'
                  icon={<FontAwesomeIcon icon='plus' />}
                  onClick={() => {
                    if (collapsible) {
                      setCollapsedOverride(false);
                    }
                    add(initialRow);
                  }}
                >
                  <FormattedMessage id={formInput.context?.addRowLabelId ?? 'table.addRow'} defaultMessage='Add row' />
                </Button>
              )}
            </Space>
          );
        }}
      </Form.List>
    </Form.Item>
  );
}

export default TableField;

type TimestampInputProps = {
  value?: string | null;
  disabled?: boolean;
  style?: React.CSSProperties;
};

function TimestampInput({ value, disabled, style }: TimestampInputProps): JSX.Element {
  const formattedValue = value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : '';
  return <Input disabled={disabled} value={formattedValue} style={style} />;
}
